
import  React from "react"
import { useState, useMemo } from "react"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"



export default function CommonTable({
  data,
  columns,
  title,
  showEntriesOptions = [10, 25, 50, 100],
  initialEntriesPerPage = 10,
  showPagination = true,
  emptyMessage = "No data available.",
}) {
  const [isMobileView, setIsMobileView] = useState(false)
  const [entriesPerPage, setEntriesPerPage] = useState(initialEntriesPerPage.toString())
  const [currentPage, setCurrentPage] = useState(1)

  const currentEntriesPerPage = Number.parseInt(entriesPerPage, 10)

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * currentEntriesPerPage
    const endIndex = startIndex + currentEntriesPerPage
    return data.slice(startIndex, endIndex)
  }, [data, currentPage, currentEntriesPerPage])

  const totalPages = useMemo(() => {
    return Math.ceil(data.length / currentEntriesPerPage)
  }, [data, currentEntriesPerPage])

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const   renderCellContent = (row, column) => {
    return column.renderCell ? column.renderCell(row) : String(row[column.key])
  }

  return (
    <div className="mx-auto bg-light-bg dark:bg-dark-bg border-t-2 rounded-t-lg border-gray-900 mx-1">
      <div className="mt-4 border-2 border-gray-300">
        <div className="p-1.5 rounded-t bg-[linear-gradient(-180deg,_#2E4B5E_0%,_#243A48_82%)] flex justify-between items-center">
          <h2 className="text-white text-sm font-semibold">{title}</h2>
          <div className="flex items-center space-x-2">
            <span className="text-white text-sm">{isMobileView ? "Card View" : "Table View"}</span>
            {/* Custom Toggle Switch */}
            <button
              type="button"
              role="switch"
              aria-checked={isMobileView}
              onClick={() => setIsMobileView(!isMobileView)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isMobileView ? "bg-gray-500" : "bg-gray-700"
              }`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-light-bg dark:bg-dark-bg shadow ring-0 transition duration-200 ease-in-out ${
                  isMobileView ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>
        <div className="bg-light-bg dark:bg-dark-bg">
          <div className="overflow-x-auto p-3">
            {showPagination && (
              <div className="bg-light-bg dark:bg-dark-bg px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-b border-gray-200">
                <div className="flex items-center space-x-2 mb-2 sm:mb-0">
                  <span className="text-gray-700 text-sm">Show</span>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(e.target.value)
                      setCurrentPage(1) // Reset to first page on entries change
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                  >
                    {showEntriesOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-700 text-sm">entries</span>
                </div>
              
              </div>
            )}

            {data.length === 0 ? (
              <div className="p-4 text-center text-gray-600">{emptyMessage}</div>
            ) : (
              <>
                {isMobileView ? (
                  <div className="grid grid-cols-1 gap-4 py-4">
                    {paginatedData.map((row, rowIndex) => (
                      <div key={row.id || rowIndex} className="border border-gray-300 shadow-sm rounded-lg">
                        <div className="p-4 space-y-2 bg-red-50">
                          {columns.map((column, colIndex) => (
                            <div key={colIndex} className="flex justify-between items-center text-sm">
                              <span className="font-semibold text-gray-700">
                                {column.mobileLabel || column.header}
                              </span>
                              <span className="text-gray-900 text-right">{renderCellContent(row, column)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="!bg-light-bg !dark:bg-dark-bg text-sm">
                        {columns.map((column, index) => (
                          <th
                            key={index}
                            className="px-4 py-3 text-left text-gray-950 font-semibold border-r border-gray-300 last:border-r-0"
                            scope="col"
                          >
                            {column.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, rowIndex) => (
                        <tr
                          key={row.id || rowIndex}
                          className="border-b border-gray-300 last:border-b-0 text-sm text-nowrap"
                        >
                          {columns.map((column, colIndex) => (
                            <td
                              key={colIndex}
                              className="px-4 py-3 text-gray-950 border-r border-gray-300 last:border-r-0 font-semibold"
                            >
                              {renderCellContent(row, column)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {showPagination && data.length > 0 && (
                          <div className="bg-light-bg dark:bg-dark-bg px-4 py-3 border-t border-gray-200">
                              
                              <div className="flex flex-col justify-center items-center space-y-4">
                                    <div className="text-sm text-gray-700">
                  Showing {data.length > 0 ? (currentPage - 1) * currentEntriesPerPage + 1 : 0} to{" "}
                  {Math.min(currentPage * currentEntriesPerPage, data.length)} of {data.length} entries
                </div>  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="First page"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm text-gray-500 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Last page"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
